import type { SyncStats } from '@viva/core';
import { useEffect, useState } from 'react';
import { DeleteAccountForm, LanguageSelect, supportLink, useAccount } from '../account/AccountGate';
import { LanguagePicker } from '../account/LanguagePicker';
import type { CatalogChoice } from '../catalog-languages';
import { useCatalog } from '../state/catalog-context';
import { languageName, t, useLanguage } from '../i18n';

/** Support contacts, the same as on the website (iptviva.com). */
const SUPPORT_TELEGRAM = 'https://t.me/IPTV1VA';
const SUPPORT_EMAIL = 'support@iptviva.com';



/** The languages of the channel list, and changing them (the list then reloads with them). */
function LanguagesPanel({ catalog, busy, onSave }: { catalog: CatalogChoice; busy: boolean; onSave: (choice: CatalogChoice) => Promise<string | null> }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  return (
    <div className="panel account-languages">
      <div className="account-card__row">
        <span className="muted">{t('account.channelLanguages')}</span>
        <strong>
          {catalog.languages.map(languageName).join(', ')}
          {catalog.international ? ` · ${t('account.international')}` : ''}
        </strong>
      </div>
      {editing ? (
        <LanguagePicker
          initial={catalog}
          busy={saving || busy}
          submitLabel={t('account.saveLanguages')}
          onSubmit={(choice) => {
            setSaving(true);
            void onSave(choice).then((failure) => {
              setSaving(false);
              setMessage(failure ?? t('account.languagesSaved'));
              if (!failure) setEditing(false);
            });
          }}
        />
      ) : (
        <button type="button" className="button" disabled={busy} onClick={() => (setEditing(true), setMessage(null))}>
          {t('account.changeLanguages')}
        </button>
      )}
      {message ? <p className="muted">{message}</p> : null}
    </div>
  );
}

function remaining(ms: number): string {
  const minutes = Math.max(0, Math.floor(ms / 60000));
  if (minutes >= 2 * 1440) return t('time.days', { count: Math.floor(minutes / 1440) });
  if (minutes >= 60) return t('time.hoursMinutes', { hours: Math.floor(minutes / 60), minutes: minutes % 60 });
  return t('time.minutes', { count: minutes });
}

/** Account details, time left, playlist status, log out. */
export function AccountScreen() {
  const { account, serverNow, logOut, reloadPlaylist, refreshSession, deleteAccount, supportWhatsapp, trialHours, catalog, setCatalog } = useAccount();
  useLanguage();
  const [deleting, setDeleting] = useState(false);
  const { sources, task, refreshSource } = useCatalog();
  const [now, setNow] = useState(serverNow);
  useEffect(() => {
    const timer = setInterval(() => setNow(serverNow()), 30_000);
    return () => clearInterval(timer);
  }, [serverNow]);

  const source = sources?.[0];
  const sync = source?.lastSync;
  const trial = account.kind === 'trial';
  const contact = supportLink(supportWhatsapp, `${t(trial ? 'account.subscribeMessage' : 'account.renewMessage')} (${account.email ?? account.username})`);
  const help = supportLink(supportWhatsapp, `${t('account.helpMessage')} (${account.email ?? account.username})`);

  return (
    <section className="screen">
      <header className="screen__header">
        <h1>{t('nav.account')}</h1>
      </header>
      <div className="panel account-card">
        <div className="account-card__row">
          <span className="muted">{account.email ? t('auth.email') : t('account.username')}</span>
          <strong>{account.email ?? account.username}</strong>
        </div>
        {account.whatsapp ? (
          <div className="account-card__row">
            <span className="muted">WhatsApp</span>
            <strong>{account.whatsapp}</strong>
          </div>
        ) : null}
        {account.clientName ? (
          <div className="account-card__row">
            <span className="muted">{t('account.name')}</span>
            <strong>{account.clientName}</strong>
          </div>
        ) : null}
        <div className="account-card__row">
          <span className="muted">{t('account.plan')}</span>
          <span className={`badge ${trial ? 'badge--trial' : account.plan === 'gold' ? 'badge--gold' : 'badge--paid'}`}>
            {trial ? t('account.freeTrial') : account.admin ? 'Admin' : account.plan === 'gold' ? 'Premium' : 'Standard'}
          </span>
        </div>
        <div className="account-card__row">
          <span className="muted">{t('account.screens')}</span>
          <strong>{account.admin ? t('account.unlimited') : account.maxStreams}</strong>
        </div>
        <div className="account-card__row">
          <span className="muted">{trial ? (account.expiresAt === null ? t('account.freeTrial') : t('account.trialEndsIn')) : t('account.validUntil')}</span>
          <strong>
            {account.expiresAt === null
              ? trial
                ? // Not started yet: the trial runs from the first time they press play.
                  t('account.trialNotStarted', { length: formatHours(trialHours) })
                : t('account.noEnd')
              : trial
                ? remaining(account.expiresAt - now)
                : `${new Date(account.expiresAt).toLocaleDateString()} (${remaining(account.expiresAt - now)})`}
          </strong>
        </div>
        {sync ? (
          <div className="account-card__row">
            <span className="muted">{t('account.content')}</span>
            <span>
              {(['live', 'movie', 'series'] as const)
                .map((kind) => (sync.pending?.includes(kind) ? t(`account.loading.${kind}`) : t(`account.count.${kind}`, { count: sync.counts[kind].toLocaleString() })))
                .join(' · ')}
            </span>
          </div>
        ) : null}
        {sync ? (
          <div className="account-card__row">
            <span className="muted">{t('account.lastLoad')}</span>
            <span>{loadTiming(sync)}</span>
          </div>
        ) : null}
      </div>
      {catalog ? <LanguagesPanel catalog={catalog} busy={task !== null} onSave={setCatalog} /> : null}
      <div className="account-actions">
        <button type="button" className="button" disabled={task !== null} onClick={() => (source ? void refreshSession().then(() => refreshSource(source)) : reloadPlaylist())}>
          {source ? t('account.updateChannels') : t('account.loadChannels')}
        </button>
        {contact ? (
          <a className="button" href={contact} target="_blank" rel="noreferrer">
            {trial ? t('account.subscribe') : t('account.renew')}
          </a>
        ) : null}
        <button type="button" className="button ghost" onClick={logOut}>
          {t('common.logOut')}
        </button>
      </div>
      <div className="panel account-app-language">
        <div className="account-card__row">
          <span className="muted">{t('account.appLanguage')}</span>
          <LanguageSelect />
        </div>
      </div>
      <div className="panel account-help">
        <h2>{t('account.help')}</h2>
        <p className="muted">{t('account.helpText')}</p>
        <div className="account-actions">
          {help ? (
            <a className="button" href={help} target="_blank" rel="noreferrer">
              WhatsApp
            </a>
          ) : null}
          <a className="button" href={SUPPORT_TELEGRAM} target="_blank" rel="noreferrer">
            Telegram
          </a>
          <a className="button" href={`mailto:${SUPPORT_EMAIL}`}>
            {t('auth.email')}
          </a>
        </div>
      </div>
      <div className="account-delete">
        {deleting ? (
          <DeleteAccountForm onDelete={deleteAccount} onCancel={() => setDeleting(false)} />
        ) : (
          <button type="button" className="gate__link" onClick={() => setDeleting(true)}>
            {t('delete.link')}
          </button>
        )}
      </div>
    </section>
  );
}

/** "1 hour", "2 hours", "30 min" */
function formatHours(hours: number): string {
  if (hours < 1) return t('time.minutes', { count: Math.round(hours * 60) });
  return t(hours === 1 ? 'time.hour' : 'time.hours', { count: hours });
}

/** How long the last channel-list load took, and where the time went (#271). */
function loadTiming(sync: SyncStats): string {
  const total = duration(sync.durationMs);
  if (!sync.timing) return total;
  return t('account.lastLoadDetail', { total, waiting: duration(sync.timing.waitingMs), download: duration(sync.timing.downloadMs), storing: duration(sync.timing.storingMs) });
}

function duration(ms: number): string {
  const seconds = Math.round(ms / 1000);
  return seconds < 60 ? `${seconds} s` : `${Math.floor(seconds / 60)} min ${seconds % 60} s`;
}
